import { useState, useEffect, useCallback, useMemo } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UNIT_TYPES = [
  { id: "studio", label: "Studio" },
  { id: "1br", label: "1 BR" },
  { id: "2br", label: "2 BR" },
  { id: "3br", label: "3 BR" },
  { id: "4br", label: "4 BR+" },
];

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const SourcedProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [caps, setCaps] = useState({});
  const [labor, setLabor] = useState({});
  const [rentMarkup, setRentMarkup] = useState(1.20);
  const [portfolioMarkup, setPortfolioMarkup] = useState(1.15);
  const [loading, setLoading] = useState(true);
  const [filterUnit, setFilterUnit] = useState("2br");
  const [filterCat, setFilterCat] = useState("all");
  const [filterNspire, setFilterNspire] = useState(false);
  const [showSafetyGuide, setShowSafetyGuide] = useState(false);
  const [backfillingSafety, setBackfillingSafety] = useState(false);
  const [maxBudget, setMaxBudget] = useState("");  // per-item budget cap (string for input control)
  const [showSupplierDir, setShowSupplierDir] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supLoading, setSupLoading] = useState(false);
  const [supCategory, setSupCategory] = useState("lighting");
  const [supDfwOnly, setSupDfwOnly] = useState(false);

  const loadSuppliers = async (cat, mp, dfw) => {
    setSupLoading(true);
    try {
      const params = new URLSearchParams({ category: cat || "all", max_price: mp || 0, dfw_only: dfw ? "true" : "false" });
      const r = await fetch(`${BACKEND_URL}/api/admin/bulk-suppliers?${params}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSuppliers(d.suppliers || []);
      }
    } catch (e) { /* silent */ }
    setSupLoading(false);
  };
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [marketRent, setMarketRent] = useState(1500);

  // Add-product form
  const [pName, setPName] = useState("");
  const [pCategory, setPCategory] = useState("vents");
  const [pSupplier, setPSupplier] = useState("");
  const [pUrl, setPUrl] = useState("");
  const [pImageUrl, setPImageUrl] = useState("");
  const [pCost, setPCost] = useState("");
  const [pTag, setPTag] = useState("");
  const [pFits, setPFits] = useState(["1br", "2br", "3br"]);
  const [pNote, setPNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sourced-products`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setProducts(d.products || []);
        setCaps(d.material_caps || {});
        setLabor(d.labor_fees || {});
        setRentMarkup(d.rent_markup || 1.20);
        setPortfolioMarkup(d.portfolio_markup || 1.15);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setErr("");
    if (!pName.trim()) { setErr("Product name is required."); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sourced-products`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: pName.trim(),
          category: pCategory,
          supplier: pSupplier.trim(),
          source_url: pUrl.trim(),
          image_url: pImageUrl.trim(),
          unit_cost: parseFloat(pCost) || 0,
          affiliate_tag: pTag.trim(),
          fits_unit_types: pFits,
          note: pNote.trim(),
        })
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        setPName(""); setPSupplier(""); setPUrl(""); setPImageUrl(""); setPCost(""); setPTag(""); setPNote("");
        setShowAdd(false);
        load();
      } else {
        setErr(d.detail || "Could not save.");
      }
    } catch {
      setErr("Network error.");
    }
    setSubmitting(false);
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Remove this product from your sourcing notebook?")) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sourced-products/${id}`, {
        method: "DELETE", credentials: "include"
      });
      if (r.ok) load();
    } catch {}
  };

  const backfillSafety = async () => {
    if (backfillingSafety) return;
    setBackfillingSafety(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/sourced-products/backfill-safety`, {
        method: "POST", credentials: "include"
      });
      if (r.ok) {
        const d = await r.json();
        alert(`✓ Material safety stamped on ${d.classified} product${d.classified === 1 ? "" : "s"}.\n${d.skipped} already had a safety class.`);
        load();
      } else {
        alert("Could not run safety backfill.");
      }
    } catch (e) { alert("Network error."); }
    setBackfillingSafety(false);
  };

  const filtered = useMemo(() => {
    const cap = parseFloat(maxBudget);
    return products.filter(p => {
      if (filterCat !== "all" && p.category !== filterCat) return false;
      if (filterUnit !== "all" && p.fits_unit_types && !p.fits_unit_types.includes(filterUnit)) return false;
      if (filterNspire && !p.nspire_safe) return false;
      if (!isNaN(cap) && cap > 0 && p.unit_cost > cap) return false;
      return true;
    });
  }, [products, filterCat, filterUnit, filterNspire, maxBudget]);

  const usedThisUnit = filtered.reduce((s, p) => s + (p.unit_cost || 0), 0);
  const capForUnit = caps[filterUnit] || 0;
  const laborForUnit = labor[filterUnit] || 0;
  const stabilizationFee = marketRent * rentMarkup;
  const portfolioFee = marketRent * portfolioMarkup;
  const margin = stabilizationFee - usedThisUnit - laborForUnit;
  const portfolioMargin = portfolioFee - usedThisUnit - laborForUnit;

  const usedPct = capForUnit > 0 ? Math.min(100, Math.round((usedThisUnit / capForUnit) * 100)) : 0;
  const overCap = usedThisUnit > capForUnit;

  return (
    <div data-testid="sourced-products-tab" style={{ padding: "0 0.5rem" }}>
      <div style={{ marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "0.85rem" }}>
        <div>
          <h1 style={{ color: "#fff", fontSize: "1.65rem", fontWeight: 800, margin: "0 0 0.4rem", letterSpacing: "-0.01em" }}>
            My Sourced Products
          </h1>
          <p style={{ color: "#ADBED1", fontSize: "0.92rem", margin: 0, lineHeight: 1.5, maxWidth: 720 }}>
            Your personal sourcing notebook. Add products you&apos;ve scouted (Fittes vents, EPODEX tile paint, premium LVP). Costs roll into the right unit-type cap. Crew sees the curated list. <strong style={{ color: "#FFB347" }}>Internal-only — clients never see caps, labor fees, or markups.</strong>
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button onClick={() => setShowSettings(true)} data-testid="btn-pricing-settings" style={{ padding: "0.6rem 1rem", background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#ADBED1", borderRadius: 6, fontWeight: 700, fontSize: "0.78rem", cursor: "pointer" }}>⚙ Caps & Labor</button>
          <button onClick={() => setShowAdd(true)} data-testid="btn-add-product" style={{ padding: "0.6rem 1.15rem", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 6, fontWeight: 800, fontSize: "0.82rem", cursor: "pointer" }}>+ Add Product</button>
        </div>
      </div>

      {/* Calculator strip */}
      <div data-testid="calc-strip" style={{ background: "linear-gradient(135deg, rgba(0,255,255,0.04) 0%, rgba(0,220,120,0.04) 100%)", border: "1px solid rgba(0,255,255,0.18)", borderRadius: 10, padding: "1.1rem 1.3rem", marginBottom: "1.25rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Unit Type</label>
            <select value={filterUnit} onChange={e => setFilterUnit(e.target.value)} data-testid="calc-unit-type" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.5rem 0.65rem", borderRadius: 5, fontSize: "0.85rem" }}>
              {UNIT_TYPES.map(u => <option key={u.id} value={u.id}>{u.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Market Rent ($)</label>
            <input type="number" value={marketRent} onChange={e => setMarketRent(parseFloat(e.target.value) || 0)} data-testid="calc-market-rent" style={{ width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", padding: "0.5rem 0.65rem", borderRadius: 5, fontSize: "0.85rem" }} />
          </div>
          <div>
            <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Material Cap</div>
            <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800 }}>{fmt(capForUnit)}</div>
          </div>
          <div>
            <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Crew Labor</div>
            <div style={{ color: "#fff", fontSize: "1.05rem", fontWeight: 800 }}>{fmt(laborForUnit)}</div>
          </div>
          <div>
            <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" }}>Stabilization Fee <span style={{ color: "#5AC8D8" }}>(Rent × {rentMarkup.toFixed(2)})</span></div>
            <div style={{ color: "#00FFFF", fontSize: "1.05rem", fontWeight: 800 }}>{fmt(stabilizationFee)}</div>
          </div>
          <div data-testid="calc-margin" style={{ background: "rgba(0,220,120,0.08)", border: "1px solid rgba(0,220,120,0.25)", padding: "0.45rem 0.65rem", borderRadius: 5 }}>
            <div style={{ color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.2rem" }}>Margin (this unit)</div>
            <div style={{ color: margin >= 0 ? "#00DC78" : "#FF8B6B", fontSize: "1.1rem", fontWeight: 800 }}>{fmt(margin)}</div>
          </div>
        </div>
        {/* Cap usage bar */}
        <div style={{ marginTop: "0.85rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
            <span style={{ color: "#ADBED1", fontSize: "0.78rem" }}>
              <strong style={{ color: "#fff" }}>{fmt(usedThisUnit)}</strong> of {fmt(capForUnit)} used <span style={{ color: "#7E92A8" }}>({usedPct}%)</span>
            </span>
            <span style={{ color: overCap ? "#FF8B6B" : "#7E92A8", fontSize: "0.74rem" }}>
              {overCap ? `⚠ Over by ${fmt(usedThisUnit - capForUnit)}` : `${fmt(capForUnit - usedThisUnit)} headroom`}
            </span>
          </div>
          <div style={{ height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
            <div data-testid="cap-bar" style={{ height: "100%", width: `${usedPct}%`, background: overCap ? "#FF8B6B" : usedPct > 80 ? "#FFB347" : "#00DC78", transition: "width 0.3s" }} />
          </div>
        </div>
        <div style={{ marginTop: "0.6rem", color: "#7E92A8", fontSize: "0.74rem", fontStyle: "italic" }}>
          Portfolio rate (10+ units): margin would be {fmt(portfolioMargin)} at Rent × {portfolioMarkup.toFixed(2)} ({fmt(portfolioFee)}). <strong style={{ color: "#FFB347" }}>Never displayed to clients.</strong>
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
        {["all", "paint", "flooring", "vents", "hardware", "fixtures", "lighting", "trim", "other"].map(c => {
          const active = filterCat === c;
          return (
            <button key={c} onClick={() => setFilterCat(c)} data-testid={`filter-cat-${c}`} style={{ padding: "0.35rem 0.85rem", background: active ? "rgba(0,255,255,0.12)" : "transparent", border: `1px solid ${active ? "#00FFFF" : "rgba(255,255,255,0.15)"}`, color: active ? "#00FFFF" : "#ADBED1", borderRadius: 5, fontSize: "0.74rem", fontWeight: 700, cursor: "pointer", textTransform: "capitalize" }}>{c}</button>
          );
        })}
      </div>

      {/* NSPIRE-Safe filter toggle + Safety Guide + Budget cap + Bulk Suppliers */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", alignItems: "center", marginBottom: "1rem", padding: "0.55rem 0.8rem", background: "rgba(122, 143, 126, 0.10)", border: "1px solid rgba(122, 143, 126, 0.30)", borderRadius: 6 }}>
        <label style={{ display: "flex", alignItems: "center", gap: "0.45rem", cursor: "pointer", fontSize: "0.82rem", fontWeight: 700, color: filterNspire ? "#86EFAC" : "#ADBED1" }}>
          <input type="checkbox" checked={filterNspire} onChange={e => setFilterNspire(e.target.checked)}
                 data-testid="filter-nspire-safe"
                 style={{ width: 16, height: 16, accentColor: "#7a8f7e", cursor: "pointer" }} />
          🛡️ NSPIRE-Safe Only
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.78rem", fontWeight: 700, color: "#ADBED1" }}>
          💰 Max $ per item:
          <input type="number" min="0" step="5" value={maxBudget}
                 onChange={e => setMaxBudget(e.target.value)}
                 placeholder="e.g. 75"
                 data-testid="max-budget-input"
                 style={{ width: 80, padding: "0.3rem 0.5rem", background: "rgba(15,23,42,0.6)", border: "1px solid #4B5563", borderRadius: 4, color: "#F3F4F6", fontSize: "0.85rem" }} />
        </label>
        <button onClick={() => { setShowSupplierDir(s => !s); if (!showSupplierDir) loadSuppliers(supCategory, maxBudget, supDfwOnly); }}
                data-testid="toggle-supplier-directory"
                style={{ background: showSupplierDir ? "#7a8f7e" : "transparent", border: "1px solid #7a8f7e", color: showSupplierDir ? "#1b2a4e" : "#86EFAC", padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
          🔌 {showSupplierDir ? "Hide" : "Show"} Bulk Suppliers
        </button>
        <button onClick={() => setShowSafetyGuide(s => !s)} data-testid="toggle-safety-guide"
                style={{ background: "transparent", border: "1px solid #86EFAC", color: "#86EFAC", padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", marginLeft: "auto" }}>
          {showSafetyGuide ? "Hide" : "Show"} Material Safety Guide
        </button>
        <button onClick={backfillSafety} disabled={backfillingSafety} data-testid="backfill-safety"
                style={{ background: "transparent", border: "1px solid #7E92A8", color: "#7E92A8", padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: backfillingSafety ? "not-allowed" : "pointer", opacity: backfillingSafety ? 0.5 : 1 }}>
          {backfillingSafety ? "Stamping…" : "↻ Classify all"}
        </button>
      </div>

      {/* Bulk Supplier Directory */}
      {showSupplierDir && (
        <div data-testid="supplier-directory-panel" style={{ background: "rgba(15,23,42,0.55)", border: "1px solid rgba(122,143,126,0.30)", borderLeft: "4px solid #B8915A", padding: "1rem 1.2rem", marginBottom: "1rem", borderRadius: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.6rem", marginBottom: "0.8rem" }}>
            <div>
              <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 800, marginBottom: 2 }}>🔌 Bulk Supplier Directory · DFW + National</div>
              <div style={{ color: "#9CA3AF", fontSize: "0.78rem" }}>One-tap deep links to pre-filtered search pages on each retailer. Sign in to Pro accounts first for bulk pricing.</div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <select value={supCategory}
                      onChange={e => { setSupCategory(e.target.value); loadSuppliers(e.target.value, maxBudget, supDfwOnly); }}
                      data-testid="supplier-category"
                      style={{ padding: "0.35rem 0.6rem", background: "rgba(15,23,42,0.6)", color: "#F3F4F6", border: "1px solid #4B5563", borderRadius: 4, fontSize: "0.78rem" }}>
                <option value="lighting">💡 Lighting</option>
                <option value="flooring">🟫 Flooring</option>
                <option value="fixtures">🚰 Fixtures</option>
                <option value="hardware">🔩 Hardware</option>
                <option value="paint">🎨 Paint</option>
                <option value="general">🛒 General</option>
                <option value="all">All</option>
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: "0.35rem", color: supDfwOnly ? "#86EFAC" : "#ADBED1", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>
                <input type="checkbox" checked={supDfwOnly}
                       onChange={e => { setSupDfwOnly(e.target.checked); loadSuppliers(supCategory, maxBudget, e.target.checked); }}
                       data-testid="supplier-dfw-only"
                       style={{ accentColor: "#7a8f7e" }} />
                DFW-local only
              </label>
              <button onClick={() => loadSuppliers(supCategory, maxBudget, supDfwOnly)}
                      disabled={supLoading}
                      data-testid="supplier-refresh"
                      style={{ background: "transparent", border: "1px solid #B8915A", color: "#B8915A", padding: "0.3rem 0.6rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}>
                {supLoading ? "…" : "↻"}
              </button>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "0.8rem" }}>
            {suppliers.map(s => {
              const primary = s.lighting_link || s.flooring_link || s.deep_link_template;
              return (
                <div key={s.id} data-testid={`supplier-card-${s.id}`} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(184,145,90,0.25)", borderRadius: 5, padding: "0.85rem 0.95rem" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.35rem" }}>
                    <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.88rem" }}>{s.name}</div>
                    {s.dfw_local && <span style={{ background: "rgba(134,239,172,0.15)", color: "#86EFAC", padding: "0.1rem 0.4rem", borderRadius: 3, fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.08em" }}>DFW</span>}
                  </div>
                  <div style={{ color: "#9CA3AF", fontSize: "0.7rem", marginBottom: "0.5rem", textTransform: "capitalize" }}>{s.supplier_type.replace(/_/g, " ")} · {s.region}</div>
                  <div style={{ color: "#D1D5DB", fontSize: "0.75rem", lineHeight: 1.45, marginBottom: "0.6rem" }}>{s.notes}</div>
                  <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                    <a href={primary} target="_blank" rel="noopener noreferrer"
                       data-testid={`supplier-shop-${s.id}`}
                       style={{ background: "#B8915A", color: "#1b2a4e", padding: "0.35rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 800, textDecoration: "none" }}>
                      🛒 Shop {maxBudget ? `(Under $${maxBudget})` : ""}
                    </a>
                    {s.pro_signup_url && (
                      <a href={s.pro_signup_url} target="_blank" rel="noopener noreferrer"
                         data-testid={`supplier-pro-${s.id}`}
                         style={{ background: "transparent", border: "1px solid #B8915A", color: "#B8915A", padding: "0.35rem 0.7rem", borderRadius: 4, fontSize: "0.72rem", fontWeight: 800, textDecoration: "none" }}>
                        Pro Signup
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            {suppliers.length === 0 && !supLoading && (
              <div style={{ color: "#7E92A8", fontSize: "0.8rem", padding: "1rem", textAlign: "center" }}>No suppliers match these filters.</div>
            )}
          </div>
        </div>
      )}

      {/* Safety Guide collapsible */}
      {showSafetyGuide && (
        <div data-testid="safety-guide-panel" style={{ background: "rgba(15,23,42,0.6)", border: "1px solid rgba(122,143,126,0.35)", borderLeft: "4px solid #7a8f7e", padding: "1rem 1.2rem", marginBottom: "1rem", borderRadius: 6, fontSize: "0.82rem", lineHeight: 1.55, color: "#D1D5DB" }}>
          <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: "#86EFAC", fontWeight: 800, marginBottom: "0.7rem" }}>🛡️ Material Safety Guide — NSPIRE / HUD-VASH / Fire-Rating</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem" }}>
            <div>
              <div style={{ color: "#86EFAC", fontWeight: 800, fontSize: "0.84rem", marginBottom: 4 }}>✅ Class A (0-25 flame spread)</div>
              <div style={{ color: "#9CA3AF", fontSize: "0.75rem" }}>Real ceramic / porcelain / stone tile, FRP panels, fiber-cement, glazed wall tile, Class-A-rated vinyl wallcovering. <strong>NSPIRE-safe everywhere</strong>, including escape routes + wet areas.</div>
            </div>
            <div>
              <div style={{ color: "#FCD34D", fontWeight: 800, fontSize: "0.84rem", marginBottom: 4 }}>⚠️ Class B (LVT/LVP — floor only)</div>
              <div style={{ color: "#9CA3AF", fontSize: "0.75rem" }}>Luxury vinyl plank / tile / sheet vinyl. Approved for FLOOR use with CFR 1630 / DOC FF-1-70 cert. NOT approved as wall covering in escape routes or wet zones.</div>
            </div>
            <div>
              <div style={{ color: "#FCA5A5", fontWeight: 800, fontSize: "0.84rem", marginBottom: 4 }}>🚫 Peel-and-Stick (Cosmetic Only)</div>
              <div style={{ color: "#9CA3AF", fontSize: "0.75rem" }}>Adhesive vinyl tile/wallpaper. Class B-C flame spread, adhesive fails behind moisture. Decorative accent on <strong>dry, non-egress walls only</strong>. Never use in HUD-VASH, Section 8, or full wet-area surrounds.</div>
            </div>
          </div>
          <div style={{ marginTop: "0.7rem", padding: "0.5rem 0.7rem", background: "rgba(252, 165, 165, 0.10)", border: "1px solid rgba(252, 165, 165, 0.30)", borderRadius: 4, fontSize: "0.74rem", color: "#FCA5A5" }}>
            <strong>Institutional rule:</strong> For VA Readiness / HUD-VASH / banker disposition files, only specify materials with documented Class A flame spread (ASTM E84). Anything classified <em>Unknown</em> below should be cleared with manufacturer cert before specifying.
          </div>
        </div>
      )}

      {/* Product grid */}
      {loading ? (
        <div data-testid="products-loading" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "3rem 1rem", gap: 10 }}>
          <div style={{ width: 26, height: 26, border: "3px solid rgba(0,200,200,0.25)", borderTopColor: "#00C8C8", borderRadius: "50%", animation: "spin 0.9s linear infinite" }}></div>
          <p style={{ color: "#7E92A8", fontSize: "0.85rem", margin: 0 }}>Loading your sourced products…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="products-empty" style={{ background: "linear-gradient(135deg, rgba(0,220,120,0.04) 0%, rgba(0,255,255,0.03) 100%)", border: "1px dashed rgba(0,220,120,0.3)", borderRadius: 12, padding: "2.5rem 1.5rem", textAlign: "center" }}>
          <div style={{ fontSize: 42, marginBottom: 8 }}>📦</div>
          <h3 style={{ color: "#fff", fontSize: "1.05rem", margin: "0 0 6px", fontWeight: 800 }}>
            {products.length === 0 ? "Your sourcing notebook is empty" : "No products match this filter"}
          </h3>
          <p style={{ color: "#ADBED1", fontSize: "0.85rem", margin: "0 0 14px", lineHeight: 1.5 }}>
            {products.length === 0
              ? "Add the materials, finishes, and fixtures you love. They become your reusable spec library — for Design Studio renders, landlord proposals, and crew briefings."
              : "Try clearing the filter, or add a new product in this category."}
          </p>
          <button
            onClick={() => setShowAdd(true)}
            data-testid="empty-add-product"
            style={{ padding: "10px 18px", background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", border: "none", color: "#030B1A", borderRadius: 8, fontWeight: 800, fontSize: "0.88rem", cursor: "pointer", boxShadow: "0 4px 12px rgba(0,220,120,0.3)" }}
          >
            + Add Your First Product
          </button>
          <p style={{ color: "#5AC8D8", fontSize: "0.74rem", margin: "16px 0 0", fontStyle: "italic" }}>
            Examples: Fittes flush wall vent · EPODEX tile paint · LVP brand you love · matte black hardware
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0.75rem" }}>
          {filtered.map(p => {
            const pctOfCap = capForUnit > 0 ? ((p.unit_cost || 0) / capForUnit * 100).toFixed(1) : 0;
            return (
              <div key={p.product_id} data-testid={`product-${p.product_id}`} style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 8, padding: "0.85rem 1rem" }}>
                {p.image_url && (
                  <div style={{ width: "100%", height: 110, background: "rgba(255,255,255,0.04)", borderRadius: 5, marginBottom: "0.6rem", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <img src={p.image_url} alt={p.name} loading="lazy" onError={(e) => { e.target.style.display = "none"; }} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }} />
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", marginBottom: "0.4rem" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#fff", fontSize: "0.92rem", fontWeight: 800, lineHeight: 1.3 }}>{p.name}</div>
                    {p.supplier && <div style={{ color: "#7E92A8", fontSize: "0.7rem", marginTop: "0.15rem" }}>{p.supplier}</div>}
                  </div>
                  <span style={{ background: "rgba(0,255,255,0.10)", color: "#00FFFF", padding: "0.15rem 0.5rem", borderRadius: 3, fontSize: "0.62rem", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>{p.category}</span>
                </div>
                {/* Material Safety Badge */}
                {p.safety_class && (() => {
                  const sc = p.safety_class;
                  const badge =
                    sc === "A" ? { bg: "rgba(134, 239, 172, 0.18)", color: "#86EFAC", text: "✅ Class A · NSPIRE-Safe" } :
                    sc === "B" ? { bg: "rgba(252, 211, 77, 0.15)", color: "#FCD34D", text: "⚠️ Class B · Floor-Only" } :
                    sc === "C" ? { bg: "rgba(252, 165, 165, 0.15)", color: "#FCA5A5", text: "🔻 Class C · Limited Use" } :
                    sc === "cosmetic_only" ? { bg: "rgba(252, 165, 165, 0.20)", color: "#FCA5A5", text: "🚫 Cosmetic Only · NOT NSPIRE" } :
                    { bg: "rgba(126, 146, 168, 0.18)", color: "#9CA3AF", text: "❔ Unknown · Get Cert" };
                  return (
                    <div title={p.safety_reasoning || ""} data-testid={`safety-badge-${p.product_id}`}
                         style={{ background: badge.bg, color: badge.color, padding: "0.25rem 0.55rem", borderRadius: 4, fontSize: "0.68rem", fontWeight: 800, marginBottom: "0.4rem", letterSpacing: "0.02em" }}>
                      {badge.text}
                      {p.fire_class && <span style={{ fontWeight: 500, opacity: 0.8, marginLeft: 6 }}>· {p.fire_class}</span>}
                    </div>
                  );
                })()}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "0.4rem" }}>
                  <span style={{ color: "#00DC78", fontSize: "1.05rem", fontWeight: 800 }}>{fmt(p.unit_cost)}</span>
                  <span style={{ color: "#7E92A8", fontSize: "0.7rem" }}>{pctOfCap}% of {filterUnit.toUpperCase()} cap</span>
                </div>
                {p.note && <p style={{ color: "#ADBED1", fontSize: "0.74rem", margin: "0 0 0.4rem", lineHeight: 1.45, fontStyle: "italic" }}>“{p.note}”</p>}
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginTop: "0.45rem" }}>
                  {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" style={{ color: "#5AC8D8", fontSize: "0.7rem", fontWeight: 700, textDecoration: "underline" }}>View source ↗</a>}
                  {p.affiliate_tag && <span style={{ color: "#A78BFA", fontSize: "0.66rem", fontWeight: 700 }}>🏷 {p.affiliate_tag}</span>}
                  <button onClick={() => removeProduct(p.product_id)} data-testid={`btn-remove-${p.product_id}`} style={{ marginLeft: "auto", background: "transparent", border: "none", color: "#FF8B6B", fontSize: "0.7rem", fontWeight: 700, cursor: "pointer" }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Product Modal */}
      {showAdd && (
        <div onClick={() => setShowAdd(false)} style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3rem 1rem", overflowY: "auto" }}>
          <div onClick={e => e.stopPropagation()} data-testid="add-product-modal" style={{ background: "#0a1929", border: "1px solid rgba(0,255,255,0.30)", borderRadius: 12, maxWidth: 600, width: "100%", padding: "1.5rem 1.75rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
              <h2 style={{ color: "#fff", fontSize: "1.2rem", fontWeight: 800, margin: 0 }}>+ Add Sourced Product</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.20)", color: "#ADBED1", padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: "0.74rem", cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem", marginBottom: "0.6rem" }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={modalLbl}>Product Name *</label>
                <input value={pName} onChange={e => setPName(e.target.value)} data-testid="prod-name" placeholder="Fittes Framed Wall Vent (4x10)" style={modalInp} />
              </div>
              <div>
                <label style={modalLbl}>Category</label>
                <select value={pCategory} onChange={e => setPCategory(e.target.value)} data-testid="prod-category" style={modalInp}>
                  {["paint", "flooring", "vents", "hardware", "fixtures", "lighting", "trim", "other"].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label style={modalLbl}>Unit Cost ($)</label>
                <input type="number" value={pCost} onChange={e => setPCost(e.target.value)} data-testid="prod-cost" placeholder="24" style={modalInp} />
              </div>
              <div>
                <label style={modalLbl}>Supplier / Brand</label>
                <input value={pSupplier} onChange={e => setPSupplier(e.target.value)} data-testid="prod-supplier" placeholder="Fittes" style={modalInp} />
              </div>
              <div>
                <label style={modalLbl}>Source URL</label>
                <input value={pUrl} onChange={e => setPUrl(e.target.value)} data-testid="prod-url" placeholder="https://fittes.com/..." style={modalInp} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={modalLbl}>Image URL <span style={{ color: "#7E92A8", fontWeight: 400, textTransform: "none", letterSpacing: "0" }}>(optional — paste from product page)</span></label>
                <input value={pImageUrl} onChange={e => setPImageUrl(e.target.value)} data-testid="prod-image-url" placeholder="https://..." style={modalInp} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={modalLbl}>Affiliate Tag <span style={{ color: "#7E92A8", fontWeight: 400, textTransform: "none", letterSpacing: "0" }}>(optional — leave blank until program approves you)</span></label>
                <input value={pTag} onChange={e => setPTag(e.target.value)} data-testid="prod-tag" placeholder="moovinforward-20" style={modalInp} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={modalLbl}>Fits Unit Types</label>
                <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                  {UNIT_TYPES.map(u => {
                    const on = pFits.includes(u.id);
                    return (
                      <button key={u.id} type="button" onClick={() => setPFits(on ? pFits.filter(x => x !== u.id) : [...pFits, u.id])} data-testid={`prod-fits-${u.id}`} style={{ padding: "0.35rem 0.75rem", background: on ? "rgba(0,255,255,0.12)" : "transparent", border: `1px solid ${on ? "#00FFFF" : "rgba(255,255,255,0.15)"}`, color: on ? "#00FFFF" : "#ADBED1", borderRadius: 4, fontSize: "0.74rem", fontWeight: 700, cursor: "pointer" }}>{u.label}</button>
                    );
                  })}
                </div>
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={modalLbl}>Why I picked this</label>
                <textarea value={pNote} onChange={e => setPNote(e.target.value)} data-testid="prod-note" rows="2" placeholder="Flush mount, paintable, $30 cheaper than Lowe's grated. Reads premium." style={{ ...modalInp, resize: "vertical", minHeight: 60, fontFamily: "inherit" }} />
              </div>
            </div>

            {err && <p data-testid="prod-error" style={{ color: "#FF8B6B", fontSize: "0.78rem", margin: "0 0 0.5rem" }}>✗ {err}</p>}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
              <button onClick={() => setShowAdd(false)} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#ADBED1", padding: "0.6rem 1rem", borderRadius: 5, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button onClick={submit} disabled={submitting || !pName.trim()} data-testid="btn-submit-product" style={{ background: pName.trim() && !submitting ? "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)" : "rgba(255,255,255,0.06)", color: pName.trim() && !submitting ? "#030B1A" : "#7E92A8", border: "none", padding: "0.6rem 1.2rem", borderRadius: 5, fontWeight: 800, fontSize: "0.82rem", cursor: pName.trim() && !submitting ? "pointer" : "not-allowed" }}>{submitting ? "Saving…" : "Save Product"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal — caps, labor, markups */}
      {showSettings && (
        <SettingsModal
          caps={caps} setCaps={setCaps}
          labor={labor} setLabor={setLabor}
          rentMarkup={rentMarkup} setRentMarkup={setRentMarkup}
          portfolioMarkup={portfolioMarkup} setPortfolioMarkup={setPortfolioMarkup}
          onClose={() => { setShowSettings(false); load(); }}
        />
      )}
    </div>
  );
};

const SettingsModal = ({ caps, setCaps, labor, setLabor, rentMarkup, setRentMarkup, portfolioMarkup, setPortfolioMarkup, onClose }) => {
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");

  const save = async () => {
    setSaving(true);
    try {
      await fetch(`${BACKEND_URL}/api/admin/sourced-products/config`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ material_caps: caps, labor_fees: labor, rent_markup: rentMarkup, portfolio_markup: portfolioMarkup })
      });
      setSavedMsg("✓ Saved");
      setTimeout(() => onClose(), 800);
    } catch {
      setSavedMsg("✗ Save failed");
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(3,11,26,0.85)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "3rem 1rem", overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} data-testid="settings-modal" style={{ background: "#0a1929", border: "1px solid rgba(255,179,71,0.30)", borderRadius: 12, maxWidth: 540, width: "100%", padding: "1.5rem 1.75rem" }}>
        <h2 style={{ color: "#fff", fontSize: "1.2rem", fontWeight: 800, margin: "0 0 0.5rem" }}>⚙ Caps, Labor & Markups</h2>
        <p style={{ color: "#ADBED1", fontSize: "0.78rem", margin: "0 0 1rem", lineHeight: 1.5 }}>
          All internal — clients never see these. Edit any value to override the defaults from your Suppliers playbook.
        </p>

        <div style={{ marginBottom: "1rem" }}>
          <h3 style={{ color: "#FFB347", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 0.5rem" }}>Material Caps (per unit)</h3>
          {UNIT_TYPES.map(u => (
            <div key={u.id} style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "center" }}>
              <label style={{ color: "#ADBED1", fontSize: "0.82rem" }}>{u.label}</label>
              <input type="number" value={caps[u.id] ?? ""} onChange={e => setCaps({ ...caps, [u.id]: parseInt(e.target.value) || 0 })} data-testid={`cap-${u.id}`} style={modalInp} />
            </div>
          ))}
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <h3 style={{ color: "#FFB347", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 0.5rem" }}>Crew Labor Fees (1099 handyman)</h3>
          {UNIT_TYPES.map(u => (
            <div key={u.id} style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: "0.5rem", marginBottom: "0.4rem", alignItems: "center" }}>
              <label style={{ color: "#ADBED1", fontSize: "0.82rem" }}>{u.label}</label>
              <input type="number" value={labor[u.id] ?? ""} onChange={e => setLabor({ ...labor, [u.id]: parseInt(e.target.value) || 0 })} data-testid={`labor-${u.id}`} style={modalInp} />
            </div>
          ))}
        </div>

        <div style={{ marginBottom: "1.25rem" }}>
          <h3 style={{ color: "#FFB347", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", margin: "0 0 0.5rem" }}>Markups (Stabilization Fee)</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.6rem" }}>
            <div>
              <label style={modalLbl}>Standard (×)</label>
              <input type="number" step="0.01" value={rentMarkup} onChange={e => setRentMarkup(parseFloat(e.target.value) || 1.20)} data-testid="rent-markup" style={modalInp} />
            </div>
            <div>
              <label style={modalLbl}>Portfolio 10+ (×)</label>
              <input type="number" step="0.01" value={portfolioMarkup} onChange={e => setPortfolioMarkup(parseFloat(e.target.value) || 1.15)} data-testid="portfolio-markup" style={modalInp} />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#7E92A8", fontSize: "0.78rem" }}>{savedMsg}</span>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={onClose} style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.18)", color: "#ADBED1", padding: "0.55rem 1rem", borderRadius: 5, fontSize: "0.78rem", fontWeight: 700, cursor: "pointer" }}>Close</button>
            <button onClick={save} disabled={saving} data-testid="btn-save-config" style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A", border: "none", padding: "0.55rem 1.2rem", borderRadius: 5, fontWeight: 800, fontSize: "0.82rem", cursor: saving ? "wait" : "pointer", opacity: saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const modalLbl = { display: "block", color: "#7E92A8", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "0.25rem" };
const modalInp = { width: "100%", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.12)", color: "#fff", padding: "0.55rem 0.75rem", borderRadius: 5, fontSize: "0.85rem", fontFamily: "inherit", boxSizing: "border-box" };

export default SourcedProductsTab;
